import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Edit, X } from 'lucide-react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const WysiwygEditor = ({ value = '', onChange, placeholder = 'Start writing...', className = '' }) => {
  console.log('WysiwygEditor: Rendered with value:', value, 'length:', value?.length);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Convert HTML to EditorState when value prop changes
  useEffect(() => {
    console.log('WysiwygEditor: useEffect triggered with value:', value, 'length:', value?.length);
    if (value && value.trim()) {
      try {
        console.log('Converting HTML to draft state:', value);
        const contentBlock = htmlToDraft(value);
        console.log('htmlToDraft result:', contentBlock);
        
        if (contentBlock && contentBlock.contentBlocks && contentBlock.contentBlocks.length > 0) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
            contentBlock.entityMap
          );
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
          console.log('Successfully created editor state with content');
        } else {
          // If htmlToDraft fails, create with plain text
          const plainText = value.replace(/<[^>]*>/g, '').trim();
          if (plainText) {
            const contentState = ContentState.createFromText(plainText);
            setEditorState(EditorState.createWithContent(contentState));
            console.log('Created editor state with plain text fallback');
          } else {
            setEditorState(EditorState.createEmpty());
          }
        }
      } catch (error) {
        console.warn('Error converting HTML to draft state:', error);
        // Fallback: create editor state with plain text
        const plainText = value.replace(/<[^>]*>/g, '').trim();
        if (plainText) {
          const contentState = ContentState.createFromText(plainText);
          setEditorState(EditorState.createWithContent(contentState));
          console.log('Used error fallback with plain text');
        } else {
          setEditorState(EditorState.createEmpty());
        }
      }
    } else {
      setEditorState(EditorState.createEmpty());
      console.log('Created empty editor state because value is empty or falsy');
    }
  }, [value]);

  // Handle editor state changes
  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
  };

  const openModal = () => {
    // Re-initialize editor state when opening modal to ensure content is loaded
    if (value && value.trim()) {
      try {
        console.log('Opening modal, re-initializing with value:', value);
        const contentBlock = htmlToDraft(value);
        if (contentBlock && contentBlock.contentBlocks && contentBlock.contentBlocks.length > 0) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
            contentBlock.entityMap
          );
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      } catch (error) {
        console.warn('Error re-initializing editor state:', error);
      }
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const saveContent = () => {
    try {
      // Convert to HTML and call onChange
      const currentContent = editorState.getCurrentContent();
      console.log('Current content state:', currentContent);
      
      // Check if there's any content
      const hasText = currentContent.hasText();
      const plainText = currentContent.getPlainText();
      console.log('Has text:', hasText, 'Plain text:', plainText);
      
      // Debug the content structure
      const blockMap = currentContent.getBlockMap();
      console.log('Block map size:', blockMap.size);
      blockMap.forEach((block, key) => {
        console.log('Block:', key, 'type:', block.getType(), 'text:', block.getText());
      });
      
      if (!hasText || !plainText.trim()) {
        console.log('No content to save, using empty string');
        if (onChange) {
          onChange('');
        }
        setIsModalOpen(false);
        return;
      }
      
      // Try different conversion approaches
      let htmlContent;
      try {
        // Convert to raw format first, then to HTML
        const rawContentState = convertToRaw(currentContent);
        console.log('Raw content state:', rawContentState);
        
        htmlContent = draftToHtml(rawContentState);
        console.log('draftToHtml from raw state:', htmlContent);
        
        // If that didn't work, try direct conversion
        if (!htmlContent || htmlContent.trim() === '') {
          console.log('Trying direct draftToHtml conversion');
          htmlContent = draftToHtml(currentContent);
          console.log('Direct draftToHtml result:', htmlContent);
        }
        
        // If still no content but we have text, create simple HTML
        if (!htmlContent || htmlContent.trim() === '') {
          const plainText = currentContent.getPlainText();
          console.log('Plain text fallback:', plainText);
          
          if (plainText && plainText.trim()) {
            // Simple paragraph conversion
            const lines = plainText.split('\n').filter(line => line.trim());
            htmlContent = lines.map(line => `<p>${line}</p>`).join('');
            console.log('Using plain text fallback HTML:', htmlContent);
          }
        }
      } catch (error) {
        console.error('Error in draftToHtml conversion:', error);
        const plainText = currentContent.getPlainText();
        if (plainText && plainText.trim()) {
          const lines = plainText.split('\n').filter(line => line.trim());
          htmlContent = lines.map(line => `<p>${line}</p>`).join('');
        }
      }
      
      console.log('Final htmlContent after all conversion attempts:', htmlContent);
      
      // Clean up the HTML output (be more conservative)
      let cleanHtml = '';
      if (htmlContent) {
        cleanHtml = htmlContent
          .replace(/<p><\/p>/g, '') // Remove completely empty paragraphs
          .replace(/<p>\s*<\/p>/g, '') // Remove paragraphs with only whitespace
          .trim();
      }
      
      // If cleanHtml is empty but we had content, use the original
      const finalHtml = cleanHtml || htmlContent || '';
      
      console.log('Cleaned HTML:', cleanHtml);
      console.log('Final HTML to save:', finalHtml);
      console.log('HTML length check - original:', htmlContent?.length, 'cleaned:', cleanHtml?.length, 'final:', finalHtml?.length);
      
      if (onChange) {
        console.log('WysiwygEditor: Calling onChange with finalHtml:', finalHtml);
        onChange(finalHtml || '');
      } else {
        console.warn('WysiwygEditor: onChange callback is not provided');
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving content:', error);
      // Fallback: get plain text
      const plainText = editorState.getCurrentContent().getPlainText();
      if (onChange && plainText.trim()) {
        onChange(`<p>${plainText}</p>`);
      }
      setIsModalOpen(false);
    }
  };

  // Strip HTML tags for display
  const stripHtml = (html) => {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p><p>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
      .trim();
  };

  const displayText = stripHtml(value);

  // Rich toolbar configuration with most common features
  const toolbarConfig = {
    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'history'],
    inline: {
      inDropdown: false,
      options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
    },
    blockType: {
      inDropdown: true,
      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
    },
    fontSize: {
      icon: undefined,
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
    },
    fontFamily: {
      options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
    },
    list: {
      inDropdown: false,
      options: ['unordered', 'ordered', 'indent', 'outdent'],
    },
    textAlign: {
      inDropdown: false,
      options: ['left', 'center', 'right', 'justify'],
    },
    colorPicker: {
      icon: undefined,
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
        'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
        'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
        'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
        'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
        'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)'],
    },
    link: {
      inDropdown: false,
      showOpenOptionOnHover: true,
      defaultTargetOption: '_self',
      options: ['link', 'unlink'],
      linkCallback: undefined,
      unlinkCallback: undefined,
    },
    history: {
      inDropdown: false,
      options: ['undo', 'redo'],
    },
  };

  return (
    <div className={className}>
      {/* Display area with edit button */}
      <div
        className="min-h-[120px] max-h-[200px] p-3 border border-gray-300 rounded-md bg-gray-50 relative group cursor-pointer overflow-hidden"
        onClick={openModal}
      >
        {value && value.trim() ? (
          <div className="relative">
            <div 
              className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap line-clamp-6"
              dangerouslySetInnerHTML={{ __html: value }}
            />
            {/* Fade overlay for long content */}
            {value.length > 200 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />
            )}
          </div>
        ) : (
          <div className="text-gray-400 italic">
            {placeholder}
          </div>
        )}
        
        {/* Edit button overlay */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openModal();
            }}
            className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-lg"
            title="Edit text"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>
        
        {/* "Click to edit" hint for long content */}
        {value.length > 200 && (
          <div className="absolute bottom-2 left-3 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
            Click to edit or read more...
          </div>
        )}
      </div>

      {/* Modal editor */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Text</h3>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Editor content */}
            <div className="p-4" style={{ minHeight: '500px' }}>
              <div className="border border-gray-300 rounded-md">
                <Editor
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  placeholder={placeholder}
                  toolbar={toolbarConfig}
                  editorClassName="rdw-editor-main"
                  toolbarClassName="rdw-editor-toolbar"
                  wrapperClassName="rdw-editor-wrapper"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveContent}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom styles for line clamping and editor styling */}
      <style jsx global>{`
        .line-clamp-6 {
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-height: 9rem;
        }
        
        /* Customize RDW editor styles */
        .rdw-editor-wrapper {
          background-color: white;
          border: none;
        }
        
        .rdw-editor-toolbar {
          border-bottom: 1px solid #e5e7eb;
          background-color: #f9fafb;
          padding: 12px;
          margin-bottom: 0;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        
        .rdw-editor-main {
          padding: 15px;
          min-height: 300px;
          max-height: 400px;
          overflow-y: auto;
          font-size: 14px;
          line-height: 1.6;
        }
        
        .rdw-editor-main:focus {
          outline: none;
        }
        
        /* Toolbar button styling */
        .rdw-option-wrapper {
          border: 1px solid #d1d5db;
          border-radius: 4px;
          margin: 0 1px;
          background-color: white;
          min-width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .rdw-option-wrapper:hover {
          background-color: #f3f4f6;
          border-color: #9ca3af;
        }
        
        .rdw-option-active {
          background-color: #dbeafe !important;
          border-color: #3b82f6 !important;
          color: #1e40af !important;
        }
        
        /* Dropdown styling */
        .rdw-dropdown-wrapper {
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background-color: white;
          height: 28px;
          min-width: 80px;
          position: relative;
          display: flex;
          align-items: center;
          margin: 0 2px;
        }
        
        .rdw-dropdown-carettoopen,
        .rdw-dropdown-carettoclose {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        }
        
        .rdw-dropdown-selectedtext {
          color: #374151;
          font-size: 12px;
          padding: 4px 20px 4px 8px;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .rdw-dropdown-optionwrapper {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
        }
        
        .rdw-dropdownoption-default {
          color: #374151;
          font-size: 12px;
          padding: 6px 8px;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .rdw-dropdownoption-default:hover {
          background-color: #f3f4f6;
        }
        
        .rdw-dropdownoption-highlighted {
          background-color: #dbeafe !important;
          color: #1e40af !important;
        }
        
        .rdw-dropdownoption-active {
          background-color: #3b82f6 !important;
          color: white !important;
        }
        
        /* Font size specific styling */
        .rdw-fontsize-wrapper .rdw-dropdown-selectedtext {
          min-width: 30px;
        }
        
        /* Font family specific styling */
        .rdw-fontfamily-wrapper .rdw-dropdown-selectedtext {
          min-width: 80px;
        }
        
        /* Color picker styling */
        .rdw-colorpicker-wrapper {
          margin: 0 2px;
        }
        
        .rdw-colorpicker-modal {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 4px !important;
        }
        
        /* Block type dropdown specific styling */
        .rdw-block-wrapper .rdw-dropdown-selectedtext {
          min-width: 60px;
        }
        
        /* Style the editor content */
        .rdw-editor-main h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        
        .rdw-editor-main h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        
        .rdw-editor-main h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        
        .rdw-editor-main ul, .rdw-editor-main ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
        
        .rdw-editor-main blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        .rdw-editor-main a {
          color: #2563eb;
          text-decoration: underline;
        }
        
        .rdw-editor-main code {
          background-color: #f3f4f6;
          padding: 2px 4px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.9em;
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;